import { FC } from 'react';
import dayjs from 'dayjs';
import GenericModal from '../../../shared/GenericModal/GenericModal';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomTextInput from '../../../shared/CustomInput/CustomTextInput';
import CustomTextArea from '../../../shared/CustomTextArea/CustomTextArea';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';
import CustomDatePicker from '../../../shared/CustomDatePicker/CustomDatePicker';
import {
  ICreateTrainingBlockBody,
  ITrainingBlock,
  IUpdateTrainingBlockBody,
  RESPONSIBLE_OPTIONS,
  TbResponsible,
} from '../../../models/allenamenti';
import { MENU_IN_PORTAL, TITLE_MAX_LENGTH, readErrorMessage } from '../allenamenti.constants';
import { useAllenamentiTaxonomy } from '../hooks/useAllenamentiTaxonomy';
import { useBlockForm } from './useBlockForm';
import GameContextFields from './GameContextFields';
import MalloFields from './MalloFields';
import styles from './BlockFormModal.module.scss';

interface IBlockFormModalProps {
  isOpen: boolean;
  teamId: string;
  /** Giorno preselezionato quando si crea da una colonna. */
  defaultDate: string;
  /** Blocco in modifica, `null` in creazione. */
  editing: ITrainingBlock | null;
  isSaving: boolean;
  onClose: () => void;
  onCreate: (body: ICreateTrainingBlockBody) => Promise<unknown>;
  onUpdate: (id: string, body: IUpdateTrainingBlockBody) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
}

/**
 * Converte la stringa "YYYY-MM-DD" del form nel `Date` locale atteso da
 * CustomDatePicker. Costruzione con orario esplicito a mezzanotte locale:
 * `new Date("YYYY-MM-DD")` verrebbe interpretato come UTC e sposterebbe il
 * giorno mostrato in fusi orari negativi rispetto a UTC.
 */
const isoToDate = (value: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Converte il `Date` scelto nel picker nella stringa "YYYY-MM-DD" del body API. */
const dateToIso = (date: Date | null): string => (date ? dayjs(date).format('YYYY-MM-DD') : '');

const BlockFormModal: FC<IBlockFormModalProps> = ({
  isOpen,
  teamId,
  defaultDate,
  editing,
  isSaving,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const {
    categories,
    isError: isTaxonomyError,
    error: taxonomyError,
    refetch: refetchTaxonomy,
  } = useAllenamentiTaxonomy();
  const {
    values,
    setField,
    subcategories,
    showGameContext,
    showMallo,
    suggestion,
    applySuggestion,
    isValid,
    toPayload,
  } = useBlockForm(categories, defaultDate, editing, isOpen);

  /**
   * Se la tassonomia non si è caricata, il select Categoria è vuoto: non ha
   * senso lasciar salvare, perché o categoryId resta "" (form non valido) o,
   * in modifica, resta quello vecchio senza che l'utente possa cambiarlo
   * consapevolmente. Meglio bloccare il salvataggio e dirlo chiaramente.
   */
  const canSave = isValid && !isTaxonomyError;

  /**
   * I campi vivono nel body della modal, ma il pulsante di salvataggio sta
   * nel Footer, cioè fuori dall'elemento <form>. CustomButton non inoltra un
   * attributo `form` al bottone nativo (non è tra le sue prop), quindi il
   * bottone chiama `save` direttamente dal suo onClick. Il `<form>` resta
   * comunque collegato a `onSubmit`, non a un `id`: l'invio da tastiera
   * (Enter su un campo di testo) funziona perché l'evento nasce sull'
   * elemento <form> stesso, non per via di un id che nessuno referenzia.
   */
  const save = async () => {
    if (!canSave) return;

    if (editing) await onUpdate(editing._id, toPayload());
    else await onCreate({ teamId, ...toPayload() });

    onClose();
  };

  const categoryOptions = categories.map((c) => ({ label: c.labelIt, value: c.slug }));
  const subcategoryOptions = subcategories.map((s) => ({ label: s.labelIt, value: s.slug }));

  const footer = (
    <div className={styles.footer}>
      {editing ? (
        <CustomButton
          color="outline"
          disabled={isSaving}
          onClick={async () => {
            await onDelete(editing._id);
            onClose();
          }}
        >
          Elimina
        </CustomButton>
      ) : (
        <span />
      )}

      <div className={styles.footerRight}>
        <CustomButton color="outline" disabled={isSaving} onClick={onClose}>
          Annulla
        </CustomButton>
        <CustomButton
          color="secondary"
          isLoading={isSaving}
          disabled={!canSave || isSaving}
          onClick={save}
        >
          Salva blocco
        </CustomButton>
      </div>
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      size="cover"
      isLoading={isSaving}
      title={editing ? 'Modifica blocco' : 'Nuovo blocco'}
      classNameBody={styles.modalBody}
      Footer={footer}
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {/* SINISTRA: tutti i parametri del blocco, impilati e raggruppati. */}
        <div className={styles.fields}>
          <p className={styles.paneLabel}>Parametri</p>

          <CustomDatePicker
            label="Giorno"
            date={isoToDate(values.localDate)}
            onChange={(date) => setField('localDate', dateToIso(date))}
            placeholder="gg/mm/aaaa"
          />

          {isTaxonomyError ? (
            <div className={styles.taxonomyError}>
              <p className={styles.taxonomyErrorText}>{readErrorMessage(taxonomyError)}</p>
              <CustomButton size="sm" color="outline" onClick={() => refetchTaxonomy()}>
                Riprova
              </CustomButton>
            </div>
          ) : (
            <CustomSelectInput
              isMulti
              label="Categorie"
              placeholder="— scegli una o più —"
              options={categoryOptions}
              value={values.categoryIds}
              onChange={(value) => setField('categoryIds', (value ?? []) as string[])}
              {...MENU_IN_PORTAL}
            />
          )}

          {!isTaxonomyError && subcategories.length > 0 && (
            <CustomSelectInput
              isMulti
              label="Sottocategorie"
              placeholder="— generico —"
              options={subcategoryOptions}
              value={values.subcategoryIds}
              onChange={(value) => setField('subcategoryIds', (value ?? []) as string[])}
              {...MENU_IN_PORTAL}
            />
          )}

          <div className={styles.row}>
            <CustomTextInput
              type="number"
              label="Durata (min)"
              min={1}
              max={600}
              value={values.durationMinutes}
              onValueNumberChange={(v) => setField('durationMinutes', v.valueAsNumber)}
            />

            <CustomSelectInput
              label="Responsabile"
              options={RESPONSIBLE_OPTIONS}
              value={values.responsible}
              onChange={(value) => setField('responsible', value as TbResponsible)}
              {...MENU_IN_PORTAL}
            />
          </div>

          {/* Campi extra: visibilità dalla tassonomia (fieldsEnabled, ADR 029). */}
          {(showGameContext || showMallo) && <div className={styles.divider} />}
          {showGameContext && <GameContextFields values={values} setField={setField} />}
          {showMallo && (
            <MalloFields
              values={values}
              setField={setField}
              suggestion={suggestion}
              onApplySuggestion={applySuggestion}
            />
          )}
        </div>

        {/* DESTRA: il titolo/descrizione è il cuore del blocco — spesso lungo e
            multiriga — quindi occupa tutta l'altezza disponibile. */}
        <div className={styles.titlePane}>
          <p className={styles.paneLabel}>Titolo / descrizione esercizio</p>
          <CustomTextArea
            placeholder="Es. Squat 3x6 - SL Squat&#10;Note, varianti, focus tecnico…"
            value={values.title}
            propsTextArea={{ maxLength: TITLE_MAX_LENGTH }}
            className={styles.titleArea}
            classNameInput={styles.titleInput}
            onChange={(e) => setField('title', e.target.value)}
          />
        </div>
      </form>
    </GenericModal>
  );
};

export default BlockFormModal;
